import {
  documentReviewLabel,
  documentValidityLabel,
  type DocumentPresentation,
  type DocumentRowState,
} from '../utils/document-card-status';
import { DocumentNoticeKindEnum, type DocumentNoticeItem, type DocumentNoticeKind } from './document-notices.types';
import { noticeSourceKeyVariant, NoticeSeverityEnum, type NoticeRegistry, type NoticeSeverity } from './notice-chip.types';

/**
 * Which detail-header chip a document belongs in, or `null` when it needs no attention.
 *
 * Order matters, most urgent first: a rejected or expired document is red even when something else
 * is also off; no file at all beats "in review" (there is nothing to review); an approved document
 * that still blocks is missing required data; "expiring" is last because it is the only kind that
 * does not block anything yet.
 */
export function documentNoticeKind(presentation: DocumentPresentation, hasFile: boolean): DocumentNoticeKind | null {
  if (presentation.reviewState === 'rejected' || presentation.validityState === 'expired') {
    return DocumentNoticeKindEnum.Blocked;
  }
  if (!hasFile || presentation.reviewState === 'missing') {
    return DocumentNoticeKindEnum.Missing;
  }
  if (presentation.reviewState === 'pending' || presentation.reviewState === 'ready') {
    return DocumentNoticeKindEnum.InReview;
  }
  if (presentation.blocksOnboarding) {
    return DocumentNoticeKindEnum.Missing;
  }
  if (presentation.validityState === 'expiring') {
    return DocumentNoticeKindEnum.Expiring;
  }
  return null;
}

/**
 * The hover-card reason after a document's name ("NIWO — verlopen"), in the Dutch the cards
 * already show (document-card-status.ts), for both documents fields. Checked in the same order as
 * `documentNoticeKind`, so a line always names the reason its chip was chosen for:
 *   1. rejected ("afgekeurd") or expired ("verlopen") — the red chip;
 *   2. no file at all ("geen bestand");
 *   3. in review ("in behandeling", "aangeleverd");
 *   4. still blocking with a file, i.e. required data incomplete ("gegevens onvolledig") —
 *      `reviewState` alone would read "goedgekeurd", exactly the misleading label a line on a chip
 *      must not show, and an upcoming expiry is not what put it on this chip;
 *   5. expiring soon ("verloopt over 12 dagen").
 */
export function documentNoticeReason(presentation: DocumentPresentation, hasFile: boolean): string {
  if (presentation.validityState === 'expired') {
    return (documentValidityLabel(presentation) ?? 'verlopen').toLowerCase();
  }
  if (presentation.reviewState !== 'rejected') {
    if (!hasFile) {
      return 'geen bestand';
    }
    if (presentation.reviewState === 'approved' && presentation.blocksOnboarding) {
      return 'gegevens onvolledig';
    }
    if (presentation.reviewState === 'approved' && presentation.validityState === 'expiring') {
      return (documentValidityLabel(presentation) ?? 'verloopt binnenkort').toLowerCase();
    }
  }
  return documentReviewLabel(presentation.reviewState).toLowerCase();
}

/**
 * A documenten entry's stored status slug as the row state the presentation is derived from —
 * the same mapping the required-documents checklist applies to its rows (`rowState`), minus the
 * pre-submit "ready" split, which only exists inside an onboarding.
 */
export function documentStateFromStatusSlug(slug: string | null | undefined): DocumentRowState {
  if (slug === 'approved' || slug === 'rejected' || slug === 'expired') {
    return slug;
  }
  return 'pending';
}

const NOTICE_KINDS: readonly DocumentNoticeKind[] = [
  DocumentNoticeKindEnum.Blocked,
  DocumentNoticeKindEnum.Missing,
  DocumentNoticeKindEnum.Expiring,
  DocumentNoticeKindEnum.InReview,
];

const KIND_SEVERITY: Readonly<Record<DocumentNoticeKind, NoticeSeverity>> = {
  [DocumentNoticeKindEnum.Blocked]: NoticeSeverityEnum.Error,
  [DocumentNoticeKindEnum.Missing]: NoticeSeverityEnum.Warning,
  [DocumentNoticeKindEnum.Expiring]: NoticeSeverityEnum.Warning,
  [DocumentNoticeKindEnum.InReview]: NoticeSeverityEnum.Info,
};

/** "1 document verlopen", "9 documenten ontbreken" — the chip text for `count` items of `kind`. */
export function documentNoticeLabel(kind: DocumentNoticeKind, items: readonly DocumentNoticeItem[]): string {
  const count = items.length;
  const one = count === 1;
  const subject = `${count} ${one ? 'document' : 'documenten'}`;
  switch (kind) {
    case DocumentNoticeKindEnum.Blocked: {
      const rejected = items.filter(item => item.rejected).length;
      const verdict = rejected === 0 ? 'verlopen' : rejected === count ? 'afgekeurd' : 'verlopen of afgekeurd';
      return `${subject} ${verdict}`;
    }
    case DocumentNoticeKindEnum.Missing:
      return `${subject} ${one ? 'ontbreekt' : 'ontbreken'}`;
    case DocumentNoticeKindEnum.Expiring:
      return `${subject} ${one ? 'verloopt' : 'verlopen'} binnenkort`;
    default:
      return `${subject} in behandeling`;
  }
}

/**
 * Publishes a documents field's notices as up to four chips — red, yellow, yellow, blue — under
 * `<baseKey>#<kind>`, and retracts every kind that has nothing to report. The tab badge still sums
 * them all: `noticeKeyPathFromSourceKey` drops the `#<kind>` suffix, so each chip still counts
 * towards the tab its field sits on.
 *
 * The plain `baseKey` is retracted too: that is where the single "wachten" chip used to live, and a
 * page that was open across a reload of this code would otherwise keep showing it next to the new
 * chips.
 */
export function publishDocumentNotices(
  registry: NoticeRegistry,
  baseKey: string,
  items: readonly DocumentNoticeItem[]
): void {
  registry.setNotice(baseKey, null);
  for (const kind of NOTICE_KINDS) {
    const ofKind = items.filter(item => item.kind === kind);
    registry.setNotice(
      noticeSourceKeyVariant(baseKey, kind),
      ofKind.length
        ? {
            severity: KIND_SEVERITY[kind],
            label: documentNoticeLabel(kind, ofKind),
            // A document without a name still counts; it just gets no blank line in the hover card.
            items: ofKind.map(item => item.line).filter(line => !!line),
            count: ofKind.length,
          }
        : null
    );
  }
}
